import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { DETAIL_METRIC_OPTIONS } from "../constants";
import { formatCurrency, formatNumber } from "../formatters";
import type { ControlStockRow, DetailMetric, DetailTable } from "../types";
import "./DetailsModal.css";

type DetailsModalProps = {
  isOpen: boolean;
  onToggle: () => void;
  detailsMetric: keyof ControlStockRow;
  setDetailsMetric: (value: keyof ControlStockRow) => void;
  detailTable: DetailTable;
  detailMetricAvailability: Record<DetailMetric, boolean>;
  allDetailRows: DetailTable["rows"];
  highlightArticleId: string | null;
};

const PAGE_SIZE = 20;
type DetailGroupMode =
  | "idMovimiento"
  | "documento"
  | "articulo"
  | "articuloVenta"
  | "articuloInverso"
  | "receta";
type DetailGroup = {
  key: string;
  label: string;
  rows: DetailTable["rows"];
  totals: { cantidad: number; monto: number };
  accordionId: string;
};

const DetailsModal = ({
  isOpen,
  onToggle,
  detailsMetric,
  setDetailsMetric,
  detailTable,
  detailMetricAvailability,
  allDetailRows,
  highlightArticleId,
}: DetailsModalProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [openGroupId, setOpenGroupId] = useState<string>("");
  const [activeGroupId, setActiveGroupId] = useState<string>("");
  const [isGroupDetailModalOpen, setIsGroupDetailModalOpen] = useState(false);
  const [groupMode, setGroupMode] = useState<DetailGroupMode>("idMovimiento");
  const isVentasMetric = detailsMetric === "ventas";
  const isInventoryDetailMetric =
    detailsMetric === "inventarioInicial" || detailsMetric === "inventarioFinal";
  const detailMetricIcons: Record<DetailMetric, string> = {
    inventarioInicial: "ri-inbox-archive-line",
    compras: "ri-shopping-cart-2-line",
    remisionPositiva: "ri-arrow-right-up-line",
    remisionNegativa: "ri-arrow-left-down-line",
    ventas: "ri-store-2-line",
    ajusteNegativo: "ri-scissors-cut-line",
    inventarioFinal: "ri-archive-line",
    teorico: "ri-calculator-line",
    diferencia: "ri-scales-3-line",
    ajustePositivo: "ri-add-circle-line",
  };

  const groupModeTabs = useMemo<
    Array<{
      value: DetailGroupMode;
      label: string;
    }>
  >(() => {
    if (isInventoryDetailMetric) {
      return [];
    }

    return [
      { value: "documento", label: "Por factura" },
      { value: "articulo", label: "Por insumo (receta)" },
      ...(isVentasMetric
        ? [
            {
              value: "articuloInverso",
              label: "Por insumo (vista inversa)",
            } as const,
          ]
        : []),
      ...(isVentasMetric
        ? [
            {
              value: "articuloVenta",
              label: "Por articulo vendido",
            } as const,
          ]
        : []),
      ...(isVentasMetric
        ? [
            {
              value: "receta",
              label: "Por receta",
            } as const,
          ]
        : []),
    ];
  }, [isInventoryDetailMetric, isVentasMetric]);

  useEffect(() => {
    if (groupModeTabs.length === 0) {
      if (groupMode !== "idMovimiento") {
        setGroupMode("idMovimiento");
      }
      return;
    }
    if (!groupModeTabs.some((tab) => tab.value === groupMode)) {
      setGroupMode(groupModeTabs[0].value);
      return;
    }
  }, [groupMode, groupModeTabs]);

  const formatArticuloIngredienteLabel = (row: DetailTable["rows"][number]) => {
    const idArticulo = (row.idArticulo ?? "").toString().trim() || "SIN-CODIGO";
    const articulo = (row.articulo ?? "").toString().trim();
    return articulo.length > 0 ? `${idArticulo} - ${articulo}` : idArticulo;
  };

  const formatRecetaLabel = (row: DetailTable["rows"][number]) => {
    const idReceta = (row.idReceta ?? "").toString().trim();
    const receta = (row.receta ?? "").toString().trim();
    if (idReceta.length > 0 && receta.length > 0) return `${idReceta} - ${receta}`;
    if (idReceta.length > 0) return idReceta;
    if (receta.length > 0) return receta;
    return "-";
  };

  const formatArticuloVentaLabel = (row: DetailTable["rows"][number]) => {
    const idArticuloVenta = (row.idArticuloVenta ?? "").toString().trim();
    const articuloVenta = (row.articuloVenta ?? "").toString().trim();

    if (idArticuloVenta.length > 0 && articuloVenta.length > 0) {
      return `${idArticuloVenta} - ${articuloVenta}`;
    }
    if (idArticuloVenta.length > 0) {
      return idArticuloVenta;
    }
    if (articuloVenta.length > 0) {
      return articuloVenta;
    }
    return "-";
  };

  const groupedRows = useMemo(() => {
    const map = new Map<
      string,
      {
        key: string;
        label: string;
        rows: DetailTable["rows"];
        totals: { cantidad: number; monto: number };
      }
    >();

    const resolveGroupKey = (row: DetailTable["rows"][number]) => {
      if (groupMode === "documento") {
        const documento = (row.documento ?? "").toString().trim();
        return documento.length > 0 && documento !== "-" ? documento : "-";
      }
      if (groupMode === "articulo" || groupMode === "articuloInverso") {
        return formatArticuloIngredienteLabel(row);
      }
      if (groupMode === "articuloVenta") {
        return formatArticuloVentaLabel(row);
      }
      if (groupMode === "receta") {
        return formatRecetaLabel(row);
      }
      const idMovimiento =
        row.idMovimiento == null || row.idMovimiento === "-"
          ? "-"
          : String(row.idMovimiento).trim();
      return idMovimiento.length > 0 ? idMovimiento : "-";
    };

    detailTable.rows.forEach((row) => {
      const key = resolveGroupKey(row);
      const group =
        map.get(key) ??
        {
          key,
          label: key,
          rows: [],
          totals: { cantidad: 0, monto: 0 },
        };
      group.rows.push(row);
      group.totals.cantidad += Number(row.cantidad ?? 0);
      group.totals.monto += Number(row.monto ?? 0);
      map.set(key, group);
    });

    const sorted = Array.from(map.values()).sort((a, b) => {
      if (a.key === "-" && b.key !== "-") {
        return 1;
      }
      if (b.key === "-" && a.key !== "-") {
        return -1;
      }
      if (groupMode === "idMovimiento") {
        return b.key.localeCompare(a.key, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      return a.key.localeCompare(b.key, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    return sorted.map((group, index) => ({
      ...group,
      accordionId: `${groupMode}-${index + 1}`,
    }));
  }, [detailTable.rows, groupMode]);

  const totalGroups = groupedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalGroups / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [detailsMetric, totalGroups, groupMode, isOpen]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedGroups = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return groupedRows.slice(start, start + PAGE_SIZE);
  }, [currentPage, groupedRows]);

  useEffect(() => {
    const firstGroupId = paginatedGroups[0]?.accordionId ?? "";
    setOpenGroupId(firstGroupId);
  }, [detailsMetric, currentPage, paginatedGroups, groupMode, isOpen]);

  const groupStart = totalGroups === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const groupEnd = Math.min(currentPage * PAGE_SIZE, totalGroups);

  const selectedGroup = useMemo(
    () =>
      paginatedGroups.find((group) => group.accordionId === openGroupId) ??
      paginatedGroups[0] ??
      null,
    [openGroupId, paginatedGroups],
  );
  const activeGroupDetail = useMemo(
    () =>
      groupedRows.find((group) => group.accordionId === activeGroupId) ?? null,
    [activeGroupId, groupedRows],
  );

  useEffect(() => {
    if (!isOpen) {
      setIsGroupDetailModalOpen(false);
      setActiveGroupId("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isGroupDetailModalOpen && !activeGroupDetail) {
      setIsGroupDetailModalOpen(false);
    }
  }, [activeGroupDetail, isGroupDetailModalOpen]);

  const getGroupLabel = (groupLabel: string) => {
    if (groupMode === "idMovimiento") {
      return `Movimiento: ${groupLabel === "-" ? "Sin ID" : groupLabel}`;
    }
    if (groupMode === "documento") {
      return `Factura: ${groupLabel === "-" ? "Sin documento" : groupLabel}`;
    }
    if (groupMode === "articuloVenta") {
      return `Articulo vendido: ${groupLabel === "-" ? "Sin dato" : groupLabel}`;
    }
    if (groupMode === "articuloInverso") {
      return `Insumo (vista inversa): ${groupLabel}`;
    }
    if (groupMode === "receta") {
      return `Receta: ${groupLabel === "-" ? "Sin receta" : groupLabel}`;
    }
    return `Insumo (receta): ${groupLabel}`;
  };

  const summarizeGroupReference = (rows: DetailTable["rows"]) => {
    const summarize = (values: Array<string | null | undefined>) => {
      const uniques = Array.from(
        new Set(
          values.map((value) => {
            const text = (value ?? "-").toString().trim();
            return text.length > 0 ? text : "-";
          }),
        ),
      );
      if (uniques.length === 0) return "-";
      if (uniques.length === 1) return uniques[0];
      return "Varios";
    };

    return {
      deposito: summarize(rows.map((row) => row.deposito)),
      fecha: summarize(rows.map((row) => row.fecha)),
      factura: summarize(rows.map((row) => row.documento)),
    };
  };

  const handleOpenGroupDetail = (group: DetailGroup) => {
    setOpenGroupId(group.accordionId);
    setActiveGroupId(group.accordionId);
    setIsGroupDetailModalOpen(true);
  };

  const handleCloseGroupDetail = () => {
    setIsGroupDetailModalOpen(false);
  };

  const normalizeMovementId = (value: string | number | null | undefined) => {
    if (value == null) return "-";
    const text = String(value).trim();
    return text.length > 0 ? text : "-";
  };

  const getRowsForDetailGroup = (group: DetailGroup) => {
    if (groupMode !== "idMovimiento") {
      return group.rows;
    }
    const targetMovementId = normalizeMovementId(group.key);
    const matchedRows = allDetailRows.filter(
      (row) => normalizeMovementId(row.idMovimiento) === targetMovementId,
    );
    return matchedRows.length > 0 ? matchedRows : group.rows;
  };

  const activeGroupRows = useMemo(() => {
    if (!activeGroupDetail) {
      return [] as DetailTable["rows"];
    }
    return getRowsForDetailGroup(activeGroupDetail);
  }, [activeGroupDetail, allDetailRows, groupMode]);

  const activeGroupTotals = useMemo(
    () =>
      activeGroupRows.reduce(
        (acc, row) => {
          acc.cantidad += Number(row.cantidad ?? 0);
          acc.total += Number(row.monto ?? 0);
          return acc;
        },
        { cantidad: 0, total: 0 },
      ),
    [activeGroupRows],
  );

  const highlightedArticleKey = (highlightArticleId ?? "").trim();

  const buildRowsGroupedByMovement = (rows: DetailTable["rows"]) => {
    const map = new Map<
      string,
      DetailTable["rows"][number] & {
        _documents: Set<string>;
        _deposits: Set<string>;
        _articulosVenta: Set<string>;
      }
    >();

    rows.forEach((row) => {
      const movementKey =
        row.idMovimiento == null || row.idMovimiento === "-"
          ? "-"
          : String(row.idMovimiento).trim() || "-";
      const current = map.get(movementKey);
      if (!current) {
        map.set(movementKey, {
          ...row,
          idMovimiento: movementKey,
          _documents: new Set(
            row.documento && row.documento !== "-" ? [String(row.documento)] : [],
          ),
          _deposits: new Set(
            row.deposito && row.deposito !== "-" ? [String(row.deposito)] : [],
          ),
          _articulosVenta:
            formatArticuloVentaLabel(row) !== "-"
              ? new Set([formatArticuloVentaLabel(row)])
              : new Set(),
        });
        return;
      }

      const nextCantidad = Number(current.cantidad ?? 0) + Number(row.cantidad ?? 0);
      const nextMonto = Number(current.monto ?? 0) + Number(row.monto ?? 0);
      const unitPrice = nextCantidad !== 0 ? nextMonto / nextCantidad : Number(current.precioUnitario ?? 0);
      const currentDate = current.fecha ?? "";
      const nextDate = row.fecha ?? "";

      if (row.documento && row.documento !== "-") {
        current._documents.add(String(row.documento));
      }
      if (row.deposito && row.deposito !== "-") {
        current._deposits.add(String(row.deposito));
      }
      const articuloVentaLabel = formatArticuloVentaLabel(row);
      if (articuloVentaLabel !== "-") {
        current._articulosVenta.add(articuloVentaLabel);
      }

      map.set(movementKey, {
        ...current,
        fecha:
          currentDate && nextDate
            ? currentDate <= nextDate
              ? currentDate
              : nextDate
            : currentDate || nextDate || null,
        deposito:
          current._deposits.size === 0
            ? "-"
            : current._deposits.size === 1
              ? Array.from(current._deposits)[0]
              : "Varios",
        documento:
          current._documents.size === 0
            ? "-"
            : current._documents.size === 1
              ? Array.from(current._documents)[0]
              : "Varios",
        articuloVenta:
          current._articulosVenta.size === 0
            ? null
            : current._articulosVenta.size === 1
              ? Array.from(current._articulosVenta)[0]
              : "Varios",
        idArticuloVenta: null,
        cantidad: nextCantidad,
        monto: nextMonto,
        precioUnitario: unitPrice,
      });
    });

    return Array.from(map.values())
      .sort((a, b) =>
        String(b.idMovimiento ?? "-").localeCompare(String(a.idMovimiento ?? "-"), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
      )
      .map(({ _documents: _doc, _deposits: _dep, _articulosVenta: _av, ...row }) => row);
  };

  const buildRowsGroupedByArticuloVenta = (rows: DetailTable["rows"]) => {
    const map = new Map<
      string,
      DetailTable["rows"][number] & {
        _documents: Set<string>;
        _deposits: Set<string>;
        _movimientos: Set<string>;
      }
    >();

    rows.forEach((row) => {
      const articuloVentaKey = formatArticuloVentaLabel(row);
      const current = map.get(articuloVentaKey);
      const movementValue =
        row.idMovimiento == null || row.idMovimiento === "-"
          ? null
          : String(row.idMovimiento).trim();

      if (!current) {
        map.set(articuloVentaKey, {
          ...row,
          articuloVenta: articuloVentaKey === "-" ? null : articuloVentaKey,
          idArticuloVenta: row.idArticuloVenta ?? null,
          _documents: new Set(
            row.documento && row.documento !== "-" ? [String(row.documento)] : [],
          ),
          _deposits: new Set(
            row.deposito && row.deposito !== "-" ? [String(row.deposito)] : [],
          ),
          _movimientos:
            movementValue && movementValue.length > 0
              ? new Set([movementValue])
              : new Set(),
        });
        return;
      }

      const nextCantidad = Number(current.cantidad ?? 0) + Number(row.cantidad ?? 0);
      const nextMonto = Number(current.monto ?? 0) + Number(row.monto ?? 0);
      const unitPrice =
        nextCantidad !== 0
          ? nextMonto / nextCantidad
          : Number(current.precioUnitario ?? 0);
      const currentDate = current.fecha ?? "";
      const nextDate = row.fecha ?? "";

      if (row.documento && row.documento !== "-") {
        current._documents.add(String(row.documento));
      }
      if (row.deposito && row.deposito !== "-") {
        current._deposits.add(String(row.deposito));
      }
      if (movementValue && movementValue.length > 0) {
        current._movimientos.add(movementValue);
      }

      map.set(articuloVentaKey, {
        ...current,
        fecha:
          currentDate && nextDate
            ? currentDate <= nextDate
              ? currentDate
              : nextDate
            : currentDate || nextDate || null,
        deposito:
          current._deposits.size === 0
            ? "-"
            : current._deposits.size === 1
              ? Array.from(current._deposits)[0]
              : "Varios",
        documento:
          current._documents.size === 0
            ? "-"
            : current._documents.size === 1
              ? Array.from(current._documents)[0]
              : "Varios",
        idMovimiento:
          current._movimientos.size === 0
            ? "-"
            : current._movimientos.size === 1
              ? Array.from(current._movimientos)[0]
              : "Varios",
        cantidad: nextCantidad,
        monto: nextMonto,
        precioUnitario: unitPrice,
      });
    });

    return Array.from(map.values())
      .sort((a, b) => {
        const keyA = formatArticuloVentaLabel(a);
        const keyB = formatArticuloVentaLabel(b);
        if (keyA === "-" && keyB !== "-") {
          return 1;
        }
        if (keyB === "-" && keyA !== "-") {
          return -1;
        }
        return keyA.localeCompare(keyB, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      })
      .map(({ _documents: _doc, _deposits: _dep, _movimientos: _mov, ...row }) => row);
  };

  const renderGroupDetailTable = (
    group: DetailGroup,
    detailRowsOverride?: DetailTable["rows"],
  ) => {
    const sourceRows = detailRowsOverride ?? group.rows;
    const baseRows =
      groupMode === "articulo"
        ? buildRowsGroupedByMovement(sourceRows)
        : groupMode === "articuloInverso"
          ? buildRowsGroupedByArticuloVenta(sourceRows)
          : sourceRows;
    const normalizeForSort = (value: string | null | undefined) => {
      const clean = (value ?? "").toString().trim();
      if (!clean || clean === "-") {
        return "~~~~";
      }
      return clean;
    };
    const rowsToRender = [...baseRows].sort((a, b) => {
      if (isVentasMetric) {
        const ventaA = normalizeForSort(formatArticuloVentaLabel(a));
        const ventaB = normalizeForSort(formatArticuloVentaLabel(b));
        const compareVenta = ventaA.localeCompare(ventaB, undefined, {
          sensitivity: "base",
          numeric: true,
        });
        if (compareVenta !== 0) {
          return compareVenta;
        }
      }

      const articuloA = normalizeForSort(a.articulo);
      const articuloB = normalizeForSort(b.articulo);
      const compareArticulo = articuloA.localeCompare(articuloB, undefined, {
        sensitivity: "base",
        numeric: true,
      });
      if (compareArticulo !== 0) {
        return compareArticulo;
      }

      return normalizeForSort(a.idArticulo).localeCompare(
        normalizeForSort(b.idArticulo),
        undefined,
        {
          sensitivity: "base",
          numeric: true,
        },
      );
    });
    const hasMovementColumn = groupMode === "articulo" || groupMode === "articuloInverso" || groupMode === "receta";
    const summarizeReferenceValue = (values: string[]) => {
      if (values.length === 0) return "-";
      if (values.length === 1) return values[0];
      return "Varios";
    };
    const referenceSummary = {
      deposito: summarizeReferenceValue(
        Array.from(
          new Set(
            rowsToRender.map((row) => {
              const value = (row.deposito ?? "-").toString().trim();
              return value.length > 0 ? value : "-";
            }),
          ),
        ),
      ),
      fecha: summarizeReferenceValue(
        Array.from(
          new Set(
            rowsToRender.map((row) => {
              const value = (row.fecha ?? "-").toString().trim();
              return value.length > 0 ? value : "-";
            }),
          ),
        ),
      ),
      factura: summarizeReferenceValue(
        Array.from(
          new Set(
            rowsToRender.map((row) => {
              const value = (row.documento ?? "-").toString().trim();
              return value.length > 0 ? value : "-";
            }),
          ),
        ),
      ),
    };
    const articuloVentaLabels = rowsToRender.map((row) => formatArticuloVentaLabel(row));
    const articuloVentaMergeInfo = rowsToRender.map((_, index) => ({
      show: true,
      rowSpan: 1,
      label: articuloVentaLabels[index] ?? "-",
    }));
    if (isVentasMetric) {
      let start = 0;
      while (start < articuloVentaLabels.length) {
        const label = articuloVentaLabels[start] ?? "-";
        let end = start + 1;
        while (end < articuloVentaLabels.length && (articuloVentaLabels[end] ?? "-") === label) {
          end += 1;
        }
        articuloVentaMergeInfo[start] = {
          show: true,
          rowSpan: end - start,
          label,
        };
        for (let idx = start + 1; idx < end; idx += 1) {
          articuloVentaMergeInfo[idx] = {
            show: false,
            rowSpan: 0,
            label,
          };
        }
        start = end;
      }
    }
    const groupTotals = rowsToRender.reduce(
      (acc, row) => {
        acc.cantidad += Number(row.cantidad ?? 0);
        acc.total += Number(row.monto ?? 0);
        return acc;
      },
      { cantidad: 0, total: 0 },
    );
    const colSpanBeforeMetrics =
      2 + (isVentasMetric ? 1 : 0) + (hasMovementColumn ? 1 : 0);
    const colSpanBeforeTotal = colSpanBeforeMetrics + 2 + (isVentasMetric ? 1 : 0);

    return (
      <>
        <div className="mb-2">
          <small className="text-muted text-uppercase fw-semibold d-block">Referencia</small>
          <div className="details-reference-line">
            <span className="details-reference-label">Depósito/Sucursal:</span>{" "}
            {referenceSummary.deposito}
          </div>
          <div className="details-reference-line">
            <span className="details-reference-label">Fecha:</span> {referenceSummary.fecha}
          </div>
          <div className="details-reference-line">
            <span className="details-reference-label">Factura:</span> {referenceSummary.factura}
          </div>
        </div>
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0 details-grid-table">
          <thead>
            <tr>
              {isVentasMetric ? <th>Articulo vendido</th> : null}
              <th>IDARTICULO</th>
              <th>ARTICULO</th>
              {hasMovementColumn ? <th>Movimiento</th> : null}
              <th className="text-end metric-col-cantidad">Cant. MP</th>
              {isVentasMetric ? <th className="text-end metric-col-cantidad">Cant. vend.</th> : null}
              <th className="text-end metric-col-costo">Costo MP U.</th>
              <th className="text-end metric-col-total">Total</th>
            </tr>
          </thead>
          <tbody>
            {rowsToRender.map((row, index) => (
              (() => {
                const isHighlighted =
                  highlightedArticleKey &&
                  String(row.idArticulo ?? "").trim() === highlightedArticleKey;
                const rowClassName = highlightedArticleKey
                  ? isHighlighted
                    ? "details-highlight-row"
                    : "details-muted-row"
                  : "";
                return (
              <tr
                key={`${detailsMetric}-${group.accordionId}-${row.idArticulo}-${index}`}
                className={rowClassName}
              >
                {isVentasMetric && articuloVentaMergeInfo[index]?.show ? (
                  <td
                    rowSpan={articuloVentaMergeInfo[index].rowSpan}
                    className="align-middle fw-semibold"
                  >
                    {articuloVentaMergeInfo[index].label}
                  </td>
                ) : null}
                <td className="fw-semibold text-uppercase">{row.idArticulo}</td>
                <td>{row.articulo}</td>
                {hasMovementColumn ? <td>{row.idMovimiento ?? "-"}</td> : null}
                <td className="text-end metric-col-cantidad">
                  {formatNumber(row.cantidad)}
                </td>
                {isVentasMetric ? (
                  <td className="text-end metric-col-cantidad">
                    {row.cantidadVendida != null ? formatNumber(row.cantidadVendida) : "-"}
                  </td>
                ) : null}
                <td className="text-end metric-col-costo">
                  {formatCurrency(row.precioUnitario)}
                </td>
                <td className="text-end metric-col-total">{formatCurrency(row.monto)}</td>
              </tr>
                );
              })()
            ))}
          </tbody>
          <tfoot>
            <tr className="details-subtotal-row">
              <th colSpan={colSpanBeforeTotal} className="text-start">
                Total grupo
              </th>
              <th className="text-end metric-col-total">{formatCurrency(groupTotals.total)}</th>
            </tr>
          </tfoot>
          </table>
        </div>
      </>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={onToggle}
        size="xl"
        centered
        className="control-stock-details-modal-dialog"
        modalClassName="control-stock-details-modal"
      >
        <ModalHeader toggle={onToggle}>
          Detalle de {DETAIL_METRIC_OPTIONS.find((opt) => opt.value === detailsMetric)?.label ?? ""}
        </ModalHeader>
        <ModalBody>
        <div className="mb-3">
          <Label className="text-muted text-uppercase small fw-semibold">
            Selecciona la columna
          </Label>
          <Nav tabs className="mb-0 flex-nowrap details-metric-tabs">
            {DETAIL_METRIC_OPTIONS.map((option) => (
              <NavItem key={option.value}>
                <NavLink
                  href="#"
                  className={`${detailsMetric === option.value ? "active" : ""} ${
                    !detailMetricAvailability[option.value] ? "disabled" : ""
                  }`}
                  disabled={!detailMetricAvailability[option.value]}
                  onClick={(event) => {
                    event.preventDefault();
                    if (!detailMetricAvailability[option.value]) {
                      return;
                    }
                    setDetailsMetric(option.value as keyof ControlStockRow);
                  }}
                >
                  <i className={`${detailMetricIcons[option.value]} me-1`}></i>
                  {option.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>
        {groupModeTabs.length > 0 && (
          <div className="mb-3">
            <Nav tabs className="mb-0">
              {groupModeTabs.map((tab) => (
                <NavItem key={tab.value}>
                  <NavLink
                    href="#"
                    className={groupMode === tab.value ? "active" : ""}
                    onClick={(event) => {
                      event.preventDefault();
                      setGroupMode(tab.value);
                    }}
                  >
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>
          </div>
        )}
        {detailTable.rows.length === 0 ? (
          <div className="text-center text-muted py-3">
            Sin movimientos para esta métrica en el rango seleccionado.
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 details-grid-table">
                <thead>
                  <tr>
                    <th>Grupo</th>
                    <th>Depósito/Sucursal</th>
                    <th>Fecha</th>
                    <th className="text-end">Mov.</th>
                    <th className="text-end metric-col-cantidad">
                      {isVentasMetric && groupMode === "receta" ? "Cant. vendida" : "Total items seleccionado"}
                    </th>
                    <th className="text-end metric-col-total">Total inventario</th>
                    <th className="text-center">ACC.</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedGroups.map((group) => {
                    const groupDisplayRows = getRowsForDetailGroup(group);
                    const reference = summarizeGroupReference(groupDisplayRows);
                    const groupDisplayTotals = groupDisplayRows.reduce(
                      (acc, row) => {
                        acc.cantidad += Number(row.cantidad ?? 0);
                        acc.total += Number(row.monto ?? 0);
                        return acc;
                      },
                      { cantidad: 0, total: 0 },
                    );
                    const selectedRows = highlightedArticleKey
                      ? groupDisplayRows.filter(
                          (row) =>
                            String(row.idArticulo ?? "").trim() === highlightedArticleKey,
                        )
                      : group.rows;
                    const useVendidaTotal = isVentasMetric && groupMode === "receta";
                    const selectedItemsTotal = selectedRows.reduce(
                      (acc, row) =>
                        acc +
                        (useVendidaTotal
                          ? Number(row.cantidadVendida ?? row.cantidad ?? 0)
                          : Number(row.cantidad ?? 0)),
                      0,
                    );
                    return (
                      <tr
                        key={`group-row-${group.accordionId}`}
                        className={
                          selectedGroup?.accordionId === group.accordionId ? "table-active" : ""
                        }
                      >
                        <td className="fw-semibold">{getGroupLabel(group.label)}</td>
                        <td>{reference.deposito}</td>
                        <td>{reference.fecha}</td>
                        <td className="text-end">{formatNumber(groupDisplayRows.length)}</td>
                        <td className="text-end metric-col-cantidad">
                          {formatNumber(selectedItemsTotal)}
                        </td>
                        <td className="text-end metric-col-total">
                          {formatCurrency(groupDisplayTotals.total)}
                        </td>
                        <td>
                          <div className="d-flex justify-content-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-soft-primary"
                              title="Ver detalle"
                              onClick={() => handleOpenGroupDetail(group)}
                            >
                              <i className="ri-search-eye-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        {totalGroups > 0 && (
          <div className="d-flex align-items-center justify-content-between mt-3">
            <small className="text-muted">
              Mostrando grupos {groupStart}-{groupEnd} de {totalGroups}
            </small>
            <div className="d-flex align-items-center gap-2">
              <Button
                color="light"
                className="border"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Anterior
              </Button>
              <small className="text-muted">
                Página {currentPage} de {totalPages}
              </small>
              <Button
                color="light"
                className="border"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
        <div className="d-flex justify-content-end mt-3">
          <div className="text-end">
	            <small className="text-muted d-block text-uppercase">Total general</small>
	            <div className="fw-semibold">
	              Cant.: {formatNumber(detailTable.totals.cantidad)} | Total:{" "}
	              {formatCurrency(detailTable.totals.monto)}
	            </div>
          </div>
        </div>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={isGroupDetailModalOpen}
        toggle={handleCloseGroupDetail}
        size="xl"
        centered
        className="control-stock-details-modal-dialog"
        modalClassName="control-stock-details-modal"
      >
        <ModalHeader toggle={handleCloseGroupDetail}>Detalle del grupo | Referencia</ModalHeader>
        <ModalBody>
          {activeGroupDetail ? (
            <>
              <div className="d-flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
                <span className="fw-semibold">{getGroupLabel(activeGroupDetail.label)}</span>
                <small className="text-muted">
                  {activeGroupRows.length} items | Cant.:{" "}
                  {formatNumber(activeGroupTotals.cantidad)} | Total:{" "}
                  {formatCurrency(activeGroupTotals.total)}
                </small>
              </div>
              {renderGroupDetailTable(activeGroupDetail, activeGroupRows)}
            </>
          ) : (
            <div className="text-center text-muted py-3">
              Sin detalle para el grupo seleccionado.
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default DetailsModal;
